import { useQuery } from '@tanstack/react-query';
import { ChevronDown, FileText } from 'lucide-react';
import { renderTemplate, type TicketDetail } from '@erp/shared';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { modelTemplatesOptions, type ModelKind } from './models-queries';

/**
 * As 8 variáveis de TEMPLATE_VARIABLE_NAMES montadas a partir do ticket já
 * carregado — mapa PLANO, mesma semântica dos montadores de `vars` da API.
 */
export function ticketTemplateVars(
  ticket: TicketDetail
): Record<string, string | number | null> {
  return {
    'ticket.number': ticket.ticketNumber,
    'ticket.title': ticket.title,
    'ticket.stage': ticket.stage.name,
    'ticket.status': ticket.status.name,
    'ticket.priority': ticket.priority?.name ?? null,
    'client.name': ticket.client.name,
    'desk.name': ticket.desk.name,
    'responsible.name': ticket.responsible?.name ?? null,
  };
}

/**
 * Dropdown "Modelo" dos composers do ticket: lista os modelos ativos da mesa
 * do ticket (+ globais) e insere `renderTemplate(body, vars)` — 100%
 * client-side. Some quando não há modelo cadastrado.
 */
export function TemplateInsertMenu({
  kind,
  ticket,
  onInsert,
}: {
  kind: ModelKind;
  ticket: TicketDetail;
  onInsert: (text: string) => void;
}) {
  const templatesQ = useQuery(modelTemplatesOptions(kind, ticket.desk.id));
  const templates = (templatesQ.data ?? []).filter((t) => t.active);

  if (templates.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <FileText className="text-muted-foreground" />
          Modelo
          <ChevronDown className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-64 w-64 overflow-y-auto">
        {templates.map((t) => (
          <DropdownMenuItem
            key={t.id}
            onSelect={() => onInsert(renderTemplate(t.body, ticketTemplateVars(ticket)))}
          >
            <span className="truncate">{t.name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
